import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { DialogFooter, DialogFormButtons } from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { trpc } from "~/lib/trpc";
import { GoogleAdsSetupInstructions } from "./google-ads-setup-instructions";

interface GoogleAdsFormProps {
  pageId: string;
  variantId: string;
  onBack: () => void;
  onCancel: () => void;
  onSuccess: () => void;
}

const FormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  rule: z.string().trim().min(1, "At least one ad keyword is required"),
});

type FormData = z.infer<typeof FormSchema>;

const GOOGLE_ADS_SETUP_LOCAL_STORAGE_KEY = "hide_google_ads_setup";

export function GoogleAdsForm({
  pageId,
  variantId,
  onBack,
  onCancel,
  onSuccess,
}: GoogleAdsFormProps) {
  const [showSetupInstructions, setShowSetupInstructions] = useState(true);
  const queryClient = useQueryClient();
  const form = useForm<FormData>({
    defaultValues: {
      name: "",
      rule: "",
    },
    resolver: zodResolver(FormSchema),
  });

  useEffect(() => {
    const hideSetup =
      localStorage.getItem(GOOGLE_ADS_SETUP_LOCAL_STORAGE_KEY) === "true";
    setShowSetupInstructions(!hideSetup);
  }, []);

  const createRuleMutation = useMutation(
    trpc.variantTrafficSources.addSources.mutationOptions({
      onSuccess: () => {
        toast.success("Traffic source created");
        queryClient.invalidateQueries({
          queryKey: trpc.variantTrafficSources.list.queryKey({
            variantId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.variants.getPaginatedVariants.queryKey({
            pageId,
          }),
        });
        if (!showSetupInstructions) {
          onSuccess();
        }
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const ruleWatch = form.watch("rule");

  useEffect(() => {
    const nameManuallyChanged = form.formState.touchedFields.name;
    if (!nameManuallyChanged && ruleWatch) {
      // Use the first line of keywords as the name if not manually changed
      const firstKeyword = ruleWatch.split("\n")[0].trim();
      if (firstKeyword) {
        form.setValue("name", firstKeyword, {
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  }, [ruleWatch, form]);

  if (createRuleMutation.isSuccess && showSetupInstructions) {
    return <GoogleAdsSetupInstructions onFinish={onSuccess} />;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (data) => {
          const rules = data.rule.split("\n").map((rule) => ({
            rule: rule.trim(),
            name: data.name,
            type: "google-ads" as const,
          }));

          if (rules.length === 0) {
            return;
          }

          await createRuleMutation.mutateAsync({
            pageId,
            variantId,
            rules,
          });
          form.reset();
        })}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input autoFocus placeholder="Ad Campaign" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rule"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ad Keywords</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter ad keywords, one per line"
                  className="min-h-[82px] max-h-[136px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <DialogFormButtons
            isPending={createRuleMutation.isPending}
            onCancel={onBack}
            submitText="Create"
            cancelText="Back"
          />
        </DialogFooter>
      </form>
    </Form>
  );
}
